import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { AdvancedTagFilter, AdvancedTagFilterCollection } from '../../../../../models';
import { Checkbox } from '../../../Checkbox';
import { DeprecatedSelect, ISelectOption } from '../../../DeprecatedSelect';
import { TagListBuilder } from '../../../entities/tags/TagListBuilder';

interface ContactFilterOptionProps {
	canEdit: boolean;
	impersonationContext?: Api.IImpersonationContext;
	skipStepContactFilter: Api.IContactsFilterRequest;
	onFilterChange: (filter: Api.IContactsFilterRequest) => void;
	stepUuid?: string;
	stepIsBusy?: boolean;
}

const filterOperatorSelectOptions: ISelectOption<Api.FilterOperator>[] = [
	{
		dataContext: Api.FilterOperator.And,
		id: 'filter-option-and',
		text: 'And contact has this tag',
	},
	{
		dataContext: Api.FilterOperator.Or,
		id: 'filter-option-or',
		text: 'And contact has at least one of these tags',
	},
];

function criteriaToFilterCollection(criteria?: Api.IContactFilterCriteria[]): AdvancedTagFilterCollection {
	if (!criteria || criteria.length === 0) {
		return AdvancedTagFilterCollection.instanceWithFilters([]);
	}
	return AdvancedTagFilterCollection.instanceWithCriteria(criteria);
}

function getOperatorFromCriteria(
	criteria?: Api.IContactFilterCriteria[]
): Api.FilterOperator.And | Api.FilterOperator.Or | null {
	if (!criteria || criteria.length === 0) {
		return null;
	}
	const firstNonNotFilter = criteria.find(x => x.op !== Api.FilterOperator.Not);
	if (firstNonNotFilter?.op === Api.FilterOperator.Or) {
		return Api.FilterOperator.Or;
	}
	if (firstNonNotFilter?.op === Api.FilterOperator.And) {
		return Api.FilterOperator.And;
	}
	return null;
}

function buildCriteriaFromCollection(
	collection: AdvancedTagFilterCollection,
	operator: Api.FilterOperator.And | Api.FilterOperator.Or | null
): Api.IContactsFilterRequest | null {
	const criteriaCollection: Api.IContactFilterCriteria[] = [];

	if (operator !== null && operator !== undefined) {
		const operatorFilters = collection.filters.filter(filter => filter.type === operator);
		if (operatorFilters.length > 0) {
			criteriaCollection.push({
				criteria: operatorFilters.map<Api.IContactFilterCriteria>(x => {
					const criteria = x.toContactFilterCriteria();
					delete criteria.op;
					return criteria;
				}),
				op: operator,
				property: Api.ContactFilterCriteriaProperty.Tag,
			});
		}
	}

	const notFilters = collection.filters.filter(filter => filter.type === Api.FilterOperator.Not);
	if (notFilters?.length > 0) {
		notFilters.forEach(x => criteriaCollection.push(x.toContactFilterCriteria()));
	}

	return criteriaCollection.length === 0 ? null : { criteria: criteriaCollection };
}

export function ContactFilterOption({
	canEdit,
	impersonationContext,
	skipStepContactFilter,
	onFilterChange,
	stepUuid,
	stepIsBusy,
}: ContactFilterOptionProps) {
	const [isEnabled, setIsEnabled] = React.useState(!!skipStepContactFilter?.criteria?.length);

	const [filterCollection, setFilterCollection] = React.useState<AdvancedTagFilterCollection>(() =>
		criteriaToFilterCollection(skipStepContactFilter?.criteria)
	);

	const [filterOperator, setFilterOperator] = React.useState<Api.FilterOperator.And | Api.FilterOperator.Or | null>(
		() => getOperatorFromCriteria(skipStepContactFilter?.criteria)
	);

	// Sync filter collection when skipStepContactFilter prop changes
	React.useEffect(() => {
		const newCollection = criteriaToFilterCollection(skipStepContactFilter?.criteria);
		setFilterCollection(prev =>
			JSON.stringify(prev.filters) !== JSON.stringify(newCollection.filters) ? newCollection : prev
		);
		setFilterOperator(prev => {
			const next = getOperatorFromCriteria(skipStepContactFilter?.criteria);
			return prev !== next ? next : prev;
		});
		setIsEnabled(prev => {
			const next = !!skipStepContactFilter?.criteria?.length;
			return prev !== next ? next : prev;
		});
	}, [skipStepContactFilter?.criteria]);

	const getTagNames = React.useCallback(
		(type: Api.FilterOperator) => {
			return filterCollection.filters.filter(filter => filter.type === type).map(f => f.tag);
		},
		[filterCollection]
	);

	const onCheckboxChanged = () => {
		if (isEnabled) {
			// Clear all filters by creating a new empty collection
			setFilterCollection(AdvancedTagFilterCollection.instanceWithFilters([]));
			setFilterOperator(null);
			setIsEnabled(false);
			onFilterChange(null);
		} else {
			setIsEnabled(true);
		}
	};

	const onSetFilterOperatorOption = React.useCallback(
		(option: ISelectOption<Api.FilterOperator.And | Api.FilterOperator.Or>, wasSelected: boolean) => {
			if (wasSelected) {
				const newOperator = option.dataContext;
				setFilterOperator(newOperator);
				// Notify parent of the operator change
				const criteria = buildCriteriaFromCollection(filterCollection, newOperator);
				onFilterChange(criteria);
			}
		},
		[filterCollection, onFilterChange]
	);

	const onTagAdded = React.useCallback(
		(tag: string, type: Api.FilterOperator) => {
			if (!filterCollection.hasValue(tag)) {
				const filter = new AdvancedTagFilter();
				filter.tag = tag;
				filter.type = type;
				// Create new collection with added filter to trigger re-render
				const newCollection = AdvancedTagFilterCollection.instanceWithFilters([...filterCollection.filters, filter]);
				setFilterCollection(newCollection);
				// Call onFilterChange with the new collection (avoids stale closure)
				const criteria = buildCriteriaFromCollection(newCollection, filterOperator);
				onFilterChange(criteria);
			} else {
				toast.error(`"${tag}" is already included as part of the filters.`);
			}
		},
		[filterCollection, filterOperator, onFilterChange]
	);

	const onTagRemoved = React.useCallback(
		(tag: string) => {
			const filter = filterCollection.filters.find(f => f.tag === tag);
			if (filter) {
				// Create new collection without the removed filter to trigger re-render
				const newFilters = filterCollection.filters.filter(f => f.tag !== tag);
				const newCollection = AdvancedTagFilterCollection.instanceWithFilters(newFilters);
				setFilterCollection(newCollection);
				// Call onFilterChange with the new collection (avoids stale closure)
				const criteria = buildCriteriaFromCollection(newCollection, filterOperator);
				onFilterChange(criteria);
			}
		},
		[filterCollection, filterOperator, onFilterChange]
	);

	const filterOperatorSelectTitle = React.useMemo(() => {
		return filterOperator === null || filterOperator === undefined ? (
			<div>Please select...</div>
		) : (
			<div>{filterOperatorSelectOptions.find(opt => opt.dataContext === filterOperator)?.text}</div>
		);
	}, [filterOperator]);

	return (
		<div>
			<Checkbox
				id={stepUuid ? `automation-step-contact-filter-${stepUuid}` : uuid()}
				checked={isEnabled}
				disabled={!canEdit || stepIsBusy}
				type='large'
				onChange={onCheckboxChanged}
			>
				<div>
					<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
						Skip if contact doesn&apos;t meet criteria
					</p>
				</div>
			</Checkbox>
			{isEnabled && (
				<div className={css(styleSheet.filterContainer)}>
					<div className={css(styleSheet.tagFiltersLabel)}>Additional filters</div>
					<DeprecatedSelect
						onOptionClick={onSetFilterOperatorOption}
						options={filterOperatorSelectOptions}
						selectedOptionTitle={filterOperatorSelectTitle}
						styles={[styleSheet.tagFiltersInput]}
					/>
					{filterOperator !== null && filterOperator !== undefined && (
						<TagListBuilder
							impersonationContext={impersonationContext}
							tags={getTagNames(filterOperator)}
							separator={filterOperator}
							onTagAdded={tag => onTagAdded(tag, filterOperator)}
							onTagRemoved={onTagRemoved}
						/>
					)}
					<div className={css(styleSheet.tagFiltersLabel)}>But exclude contacts that have...</div>
					<TagListBuilder
						impersonationContext={impersonationContext}
						tags={getTagNames(Api.FilterOperator.Not)}
						separator={Api.FilterOperator.Not}
						onTagAdded={tag => onTagAdded(tag, Api.FilterOperator.Not)}
						onTagRemoved={onTagRemoved}
					/>
				</div>
			)}
		</div>
	);
}

const styleSheet = StyleSheet.create({
	checkboxText: {
		color: '#4a4a4a',
		fontSize: 14,
		marginBottom: 2,
		marginLeft: 16,
		marginTop: 0,
		width: 500,
	},
	filterContainer: {
		marginLeft: 32,
		marginTop: 12,
	},
	noMargin: {
		marginBottom: 0,
	},
	tagFiltersInput: {
		maxWidth: 'auto',
		minWidth: 380,
		width: '100%',
	},
	tagFiltersLabel: {
		color: '#4a4a4a',
		fontSize: 14,
		marginTop: 12,
	},
});
